import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import { api, getStoredToken, setAuthToken } from "../api/client";
import type { AuthUser } from "../api/types";

interface AuthContextValue {
  user: AuthUser | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<{ requiresInitials: boolean }>;
  submitSessionInitials: (initials: string) => Promise<void>;
  logout: () => void;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = getStoredToken();
    if (!token) {
      setLoading(false);
      return;
    }
    api
      .get<AuthUser>("/auth/me")
      .then((res) => setUser(res.data))
      .catch(() => setAuthToken(null))
      .finally(() => setLoading(false));
  }, []);

  /**
   * Two-step for a group with requireLoginInitials on (the "Get User
   * Initials" Group setup checkbox): this stores a token but does NOT set
   * `user` yet when requiresInitials comes back true, so the app still
   * treats the person as logged out. LoginPage.tsx shows the initials step
   * next and finishes login via submitSessionInitials, which is what
   * actually sets `user`. This is a UX nicety, not the real enforcement -
   * authorizeVisit on the backend independently blocks Authorize if a group
   * requires initials and the token doesn't have them.
   */
  async function login(email: string, password: string) {
    const res = await api.post<{ token: string; requiresInitials: boolean; user: AuthUser }>("/auth/login", { email, password });
    setAuthToken(res.data.token);
    if (res.data.requiresInitials) {
      return { requiresInitials: true };
    }
    setUser(res.data.user);
    return { requiresInitials: false };
  }

  async function submitSessionInitials(initials: string) {
    const res = await api.post<{ token: string; user: AuthUser }>("/auth/session-initials", { initials });
    setAuthToken(res.data.token);
    setUser(res.data.user);
  }

  function logout() {
    setAuthToken(null);
    setUser(null);
  }

  return (
    <AuthContext.Provider value={{ user, loading, login, submitSessionInitials, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within an AuthProvider");
  return ctx;
}